import { Maybe } from '@/types/globals';
import { Injectable } from '@angular/core';
import { SpecificationEnum } from '@core/procedure-helpers/models/specification.enum';
import { LoggerService } from '@core/services/logger/logger.service';
import { DentureDetailsQuery } from '@modules/denture-details/state/denture-details.query';
import { OrderQuery } from '@modules/order/state/order-query';
import { PatientQuery } from '@modules/patient/state/patient-query';
import { Tooth } from '@modules/teeth-diagram/models/tooth';
import { UnitTypesInBridge } from '@modules/teeth-diagram/models/unit-type-in-bridge.enum';
import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';
import { Doctor } from '@shared/models/doctor';
import { PopupIconNames } from '@shared/models/enums/popup-icon.enum';
import { PopUpActions } from '@shared/models/enums/popup-modal-actions.enum';
import { IdName } from '@shared/models/id-name';
import { MaterialOption } from '@shared/models/material-option';
import { ProcedureMap } from '@shared/models/procedure-map';
import { OrderInformationModel } from '@shared/models/rx-models/interfaces/order-information-model';
import { PatientModelDto } from '@shared/models/rx-models/interfaces/patient-model-dto';
import { RxModel } from '@shared/models/rx-models/interfaces/rx-model';
import { RxTemplateService } from '@shared/rx-templates/rx-template.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { ValidateArtRxService } from '@modules/rx-intuitive-art/services/validate-art-rx.service';
import { Observable } from 'rxjs';
import { BridgeService } from './bridge.service';
import { OrderValidationService } from './order-validation.service';
import { PopupService } from './popup.service';
import { RxMaterialRulesService } from './rx-rules-helper/rx-material-rules.service';

interface ValidationPair {
	isMandatory: (t: Tooth) => boolean;
	isInvalid: (t: Tooth) => boolean;
}

@Injectable({ providedIn: 'root' })
export class ValidateRxService {
	private name = 'ValidateRxService';

	constructor(
		private popupService: PopupService,
		private bridgeService: BridgeService,
		private rxMaterialRulesService: RxMaterialRulesService,
		private patientQuery: PatientQuery,
		private shellQuery: ShellQuery,
		private orderQuery: OrderQuery,
		private readonly logger: LoggerService,
		private readonly rxTemplateService: RxTemplateService,
		private readonly orderValidationService: OrderValidationService,
		private readonly validateArtRxService: ValidateArtRxService
	) {}

	validateRxForSave({
		patient,
		caseType,
		currentAlignerId,
		procedureMap,
		doctor,
		rx
	}: {
		patient: Maybe<PatientModelDto>;
		caseType: Maybe<IdName>;
		currentAlignerId: Maybe<string>;
		procedureMap: Maybe<ProcedureMap>;
		doctor: Maybe<Doctor>;
		rx: Maybe<RxModel>;
	}): boolean {
		const isOrderValid = this.orderValidationService.isOrderValid(caseType, currentAlignerId, procedureMap);
		const isDoctorValid = this.isDoctorValid(doctor);
		const isPatientValid = this.isPatientValid(patient || rx?.Patient);
		const isRxAttachmentsReady = this.shellQuery.isRxAttachmentsReady;

		return isOrderValid && isDoctorValid && isPatientValid && isRxAttachmentsReady;
	}

	isOrderSectionValidForSend(): boolean {
		return this.orderValidationService.isOrderSectionValidForSend();
	}

	isPatientValid(patient: Maybe<PatientModelDto>): boolean {
		const isPatientInConflict = this.patientQuery.isPatientInConflict;

		return !!patient?.FirstName && !!patient?.LastName && !isPatientInConflict;
	}

	isDoctorValid(doctor: Maybe<Doctor>): boolean {
		return !!doctor?.Licence && !!doctor?.Id;
	}

	openValidationPopup(): Observable<PopUpActions> {
		const popUpInput = {
			titleTranslationKey: 'Popup.Confirmation',
			contentTranslationKey: 'Validation.InvalidRxMessage',
			iconName: PopupIconNames.Question
		};

		return this.popupService.openConfirmationPopUp({ popUpInput });
	}

	isOrderInfoInvalidForSend(orderInfo: Maybe<OrderInformationModel>): boolean {
		const isOrderInfoInvalid =
			!orderInfo ||
			((orderInfo.LocalIdeCadCamSystemId === null ||
				orderInfo.LocalIdeCadCamSystemId === undefined ||
				orderInfo.LocalIdeCadCamSystemId <= 0) &&
				(orderInfo.NumOfModels === null || orderInfo.NumOfModels === undefined || orderInfo.NumOfModels <= 0));

		if (isOrderInfoInvalid) {
			this.logger.info('Rx has invalid order information for send.', {
				module: this.name
			});
		}

		return isOrderInfoInvalid;
	}

	isTreatmentInvalidForSend(tooth: Tooth): boolean {
		if (this.validateArtRxService.isArtProcedure) {
			return this.validateArtRxService.isTreatmentInvalidForSend(tooth);
		}

		if (this.shellQuery.isProcedureFlow) {
			return this.isTreatmentInvalidForSendV1(tooth);
		}

		return this.isTreatmentInvalidForSendV0(tooth);
	}

	isCrownSectionInvalidForSend(tooth: Tooth): boolean {
		if (this.validateArtRxService.isArtProcedure) {
			return false;
		}

		return this.isCrownSectionInvalidV1(tooth);
	}

	isTreatmentInvalidForSendV1(tooth: Tooth): boolean {
		const config = tooth.BridgeIndex ? this.getTreatmentValidationInBridgeConfig() : this.getTreatmentValidationConfig();

		for (const { isMandatory, isInvalid } of Object.values(config)) {
			if (isMandatory(tooth) && isInvalid(tooth)) {
				this.logger.info(`Tooth ${tooth.ToothID} is mandatory but invalid`, {
					module: this.name
				});

				return true;
			}
		}

		return false;
	}

	isDentureDetailsInvalidForSend(dentureDetails: DentureDetailsQuery): boolean {
		if (this.rxTemplateService.currentTemplate.behaviors.validation.items.dentureDetails.disabled) {
			return false;
		}

		const isDentureDetailsInfoInvalid = !dentureDetails.shadeSystem || !dentureDetails.teethShade || !dentureDetails.gingival;

		if (isDentureDetailsInfoInvalid) {
			this.logger.info('Rx has invalid order information for send.', {
				module: this.name
			});
		}

		return isDentureDetailsInfoInvalid;
	}

	isDentureDetailsValidForSend(dentureDetails: DentureDetailsQuery): boolean {
		return !this.isDentureDetailsInvalidForSend(dentureDetails);
	}

	isTreatmentInvalidForSendV0(tooth: Tooth): boolean {
		const isBridgeTooth = !tooth.UnitTypeID;
		const unitTypeWithoutValidation =
			tooth.UnitTypeID &&
			[UnitTypes.Detachable, UnitTypes.Missing_edentulousSpace, UnitTypes.Missing_noSpace].includes(tooth.UnitTypeID);

		if (
			(isBridgeTooth && this.bridgeService.isPonticMDAbutmentOrMissing(tooth.ToothInBridgeTypeID)) ||
			(!isBridgeTooth && unitTypeWithoutValidation)
		) {
			return false;
		}

		if (!this.isCrownSectionValidationDisabled && (!tooth.MaterialID || tooth.MaterialID === -1)) {
			this.logger.info(`Invalid material for the tooth ${tooth.ToothID}`, {
				module: this.name
			});

			return true;
		}

		const unitTypeId = isBridgeTooth
			? this.bridgeService.convertFromBridgeToothUnitType({ unitTypeInBridge: tooth.ToothInBridgeTypeID })
			: tooth.UnitTypeID;

		const materialOption = this.isCrownSectionValidationDisabled
			? this.getNonRequiredMaterialOptions()
			: // @ts-expect-error - TODO: remove this and fix type error after updating Tooth model
				this.rxMaterialRulesService.getMaterialUnitTypeOption({ materialId: tooth.MaterialID, unitTypeId });

		const isInvalidImplantType = unitTypeId === UnitTypes.ScanBody && this.isImplantsInvalid(tooth);

		if (isInvalidImplantType) {
			this.logger.info(`Invalid treatment for the tooth ${tooth.ToothID}. isInvalidImplantType: true`, {
				module: this.name
			});

			return true;
		}

		if (!materialOption) {
			this.logger.info(`Invalid treatment for the tooth ${tooth.ToothID}. NO materialOption`, {
				module: this.name
			});

			return true;
		}

		const isInvalidShadeBody = !!materialOption.BodyShadeMandatory && !tooth.ShadeBody;
		const isInvalidMarginDesign =
			!!materialOption.MarginDesignMandatory && (!tooth.MarginDesignLingualId || !tooth.MarginDesignBuccalId);
		const isInvalidPrepDesign =
			!!materialOption.PrepDesignMandatory && (!tooth.PreparationDesignBuccalId || !tooth.PreparationDesignLingualId);
		const isInvalidStumpShade = !!materialOption.StumpShadeMandatory && !tooth.StumpfShade;

		const isInvalid = isInvalidShadeBody || isInvalidMarginDesign || isInvalidPrepDesign || isInvalidStumpShade;

		if (isInvalid) {
			this.logger.info(
				`Invalid treatment for the tooth ${tooth.ToothID}. ` +
					`isInvalidShadeBody: ${isInvalidShadeBody} ` +
					`isInvalidMarginDesign: ${isInvalidMarginDesign} ` +
					`isInvalidPrepDesign: ${isInvalidPrepDesign} ` +
					`isInvalidStumpShade: ${isInvalidStumpShade} `,
				{
					module: this.name
				}
			);
		}

		return isInvalid;
	}

	private isCrownSectionInvalidV1(tooth: Tooth): boolean {
		if (!tooth.MaterialID || tooth.MaterialID === -1) {
			return true;
		}

		// material group is needed for custom materials when they weren't loaded
		const materialOption = this.rxMaterialRulesService.getMaterialSpecOption(
			tooth.MaterialID,
			tooth.SpecificationId ?? SpecificationEnum.None,
			tooth.CustomLabMaterialGroup ?? undefined
		);

		if (!materialOption) {
			return true;
		}

		return (
			(!!materialOption.BodyShadeMandatory && !tooth.ShadeBody) ||
			(!!materialOption.MarginDesignMandatory && (!tooth.MarginDesignLingualId || !tooth.MarginDesignBuccalId)) ||
			(!!materialOption.PrepDesignMandatory && (!tooth.PreparationDesignBuccalId || !tooth.PreparationDesignLingualId)) ||
			(!!materialOption.StumpShadeMandatory && !tooth.StumpfShade)
		);
	}

	private getTreatmentValidationConfig(): Record<string, ValidationPair> {
		const isMandatory = (t: Tooth) => ![UnitTypes.Detachable, UnitTypes.SupportingTooth, UnitTypes.Missing].includes(t.UnitTypeID ?? 0);
		const isImplantPosition = (t: Tooth) => t.UnitTypeID === UnitTypes.ImplantPosition;

		return {
			forCrown: {
				isMandatory: (t: Tooth) => isMandatory(t) && !isImplantPosition(t) && this.isCrownMandatory(),
				isInvalid: (t: Tooth) => this.isCrownSectionInvalidV1(t)
			},
			forScanBody: {
				// Implant Planning (ImplantPosition) only requires an implant manufacturer, same popup fields as ImplantBased otherwise.
				isMandatory: (t: Tooth) =>
					isMandatory(t) &&
					(UnitTypes.ImplantBased === t.UnitTypeID || isImplantPosition(t)) &&
					!this.isScanBodyValidationDisabled,
				isInvalid: (t: Tooth) => this.isImplantsInvalid(t)
			}
		};
	}

	private getTreatmentValidationInBridgeConfig(): Record<string, ValidationPair> {
		const isMandatory = (t: Tooth) => ![UnitTypesInBridge.Missing].includes(t.ToothInBridgeTypeID ?? 0);

		return {
			forCrown: {
				isMandatory: (t: Tooth) => isMandatory(t) && this.isCrownMandatory(),
				isInvalid: (t: Tooth) => this.isCrownSectionInvalidV1(t)
			},
			forScanBody: {
				isMandatory: (t: Tooth) =>
					isMandatory(t) && t.ToothInBridgeTypeID === UnitTypesInBridge.ImplantBased && !this.isScanBodyValidationDisabled,
				isInvalid: (t: Tooth) => this.isImplantsInvalid(t)
			}
		};
	}

	private isCrownMandatory(): boolean {
		return !(this.orderQuery.isDentureOrderV1 || this.isCrownSectionValidationDisabled);
	}

	private get isCrownSectionValidationDisabled(): boolean {
		return !!this.rxTemplateService.currentTemplate.behaviors.validation.items.crownSection.disabled;
	}

	private get isScanBodyValidationDisabled(): boolean {
		return !!this.rxTemplateService.currentTemplate.behaviors.validation.items.scanBody.disabled;
	}

	private getNonRequiredMaterialOptions(): MaterialOption {
		return {
			StumpShadeMandatory: false,
			BodyShadeMandatory: false,
			MarginDesignMandatory: false,
			PrepDesignMandatory: false
		};
	}

	private isImplantsInvalid(tooth: Tooth): boolean {
		if (tooth.ImplantTypeID && !tooth.ImplantsViewData) {
			return false;
		}

		if (!tooth.ImplantTypeID && tooth.ImplantsViewData && !this.isImplantsViewDataInvalid(tooth)) {
			return false;
		}

		return true;
	}

	private isImplantsViewDataInvalid(tooth: Tooth): boolean {
		if (tooth.UnitTypeID === UnitTypes.ImplantPosition) {
			// Implant Planning popup only shows the manufacturer selector (no Connection/Platform/Scan body).
			return !tooth.ImplantsViewData?.ImplantManufacturerId;
		}

		return (
			!tooth.ImplantsViewData?.ImplantManufacturerId ||
			!tooth.ImplantsViewData?.ImplantConnectionId ||
			!tooth.ImplantsViewData?.ImplantPlatformId ||
			!tooth.ImplantsViewData?.ScanBodyId
		);
	}
}
